import React, { forwardRef, RefObject, useEffect } from 'react';
import cs from 'classnames';
import { Template } from '../../domain/entities/template';
import { TOP_CONVERTING_CATEGORY_SLUG } from '../../config';
import { useInViewPort } from '../../hooks/useInViewPort';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { TemplateImage } from '../TemplateImage/TemplateImage';
import { ViewButton } from '../ViewButton/ViewButton';
import { EditButton } from '../EditButton/EditButton';
import { topConvertingTemplateDataHooks as dataHooks } from './TopConvertingTemplate.dataHooks';
import s from './TopConvertingTemplate.scss';

interface TopConvertingTemplateProps {
  template: Template;
  className?: string;
  index?: number;
}

export const TopConvertingTemplate = forwardRef<
  HTMLDivElement,
  TopConvertingTemplateProps
>(({ template, className, index }, ref) => {
  const biLogger = useBiLogger();
  const inViewPort = useInViewPort(ref as RefObject<HTMLDivElement>, {
    threshold: 1,
  });

  useEffect(() => {
    if (!inViewPort) {
      return;
    }
    biLogger.reportAboveFoldImpression({
      templateId: template.templateId,
      slug: template.slug,
      title: template.title,
      categorySlug: TOP_CONVERTING_CATEGORY_SLUG,
      index,
    });
  }, [biLogger, inViewPort, index, template]);

  return (
    <div className={cs(s.container, className)} ref={ref}>
      <div className={s.texts}>
        <div className={s.title}>{template.title}</div>
        <div className={s.description}>{template.description}</div>
      </div>
      <div className={s.imageContainer}>
        <TemplateImage
          className={s.image}
          template={template}
          categorySlug={TOP_CONVERTING_CATEGORY_SLUG}
          index={index}
          isLazyLoad={false}
        />
      </div>
      <div className={s.actions}>
        <ViewButton
          skin="light"
          size="small"
          template={template}
          categorySlug={TOP_CONVERTING_CATEGORY_SLUG}
          index={index}
          className={s.viewButton}
          dataHook={dataHooks.viewButton({ key: template.templateId })}
        />
        <EditButton
          skin="light"
          size="small"
          priority="secondary"
          template={template}
          categorySlug={TOP_CONVERTING_CATEGORY_SLUG}
          index={index}
          className={s.editButton}
          dataHook={dataHooks.editButton({ key: template.templateId })}
        />
      </div>
    </div>
  );
});
